import { useEffect, useState } from 'react';
import AjudaTela from '../../components/AjudaTela';
import TelaPermissoesGrid from '../../components/TelaPermissoesGrid';
import { api } from '../../services/api';

const VERDE = '#1a3d2a';
const AZUL = '#1e40af';

const AJUDA = {
  titulo: 'Perfis de Acesso',
  descricao:
    'Um perfil é um conjunto de permissões (o que pode ler, criar, editar e excluir em cada tela) que você monta uma vez e reaplica em vários colaboradores. Editar um perfil aqui não muda quem já recebeu essas permissões antes — é preciso reaplicar o perfil na tela de Usuários pra atualizar.',
  secoes: [
    {
      titulo: 'Perfil',
      campos: [
        { nome: 'Nome', obrigatorio: true, explicacao: 'Nome que vai aparecer na lista de perfis ao criar/editar um colaborador (ex.: "Financeiro", "Colheita").' },
        { nome: 'Permissões por tela', obrigatorio: false, explicacao: 'Marque leitura/criação/edição/exclusão tela por tela. Deixe tudo desmarcado para um perfil sem acesso a nada além do login.' },
      ],
    },
  ],
  dicas: [
    'Esta empresa já vem com 6 perfis padrão (Proprietário, Diretor, Gestor, Agrônomo, RH, Operador) — edite-os ou crie os seus próprios.',
    'Perfis são exclusivos desta empresa — nunca aparecem nem podem ser usados por outra empresa do sistema.',
  ],
};

const asLista = (data) => (Array.isArray(data) ? data : data?.results ?? []);

export default function PerfisAcesso() {
  const [perfis, setPerfis] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [nome, setNome] = useState('');
  const [valoresTela, setValoresTela] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [modalNovo, setModalNovo] = useState(false);
  const [nomeNovo, setNomeNovo] = useState('');

  useEffect(() => {
    carregarTudo();
  }, []);

  const carregarTudo = async () => {
    setCarregando(true);
    try {
      const [resPerfis, resModulos] = await Promise.all([
        api.get('/configuracoes/perfis/'),
        api.get('/configuracoes/modulos/'),
      ]);
      const lista = asLista(resPerfis.data);
      setPerfis(lista);
      setModulos(asLista(resModulos.data));
      if (lista.length > 0) selecionarPerfil(lista[0]);
    } catch {
      setErro('Erro ao carregar perfis de acesso.');
    } finally {
      setCarregando(false);
    }
  };

  const mapaDeTelas = (telas) => {
    const mapa = {};
    (telas || []).forEach((t) => { mapa[t.tela] = t; });
    return mapa;
  };

  const selecionarPerfil = (perfil) => {
    setSelecionado(perfil);
    setNome(perfil.nome);
    setValoresTela(mapaDeTelas(perfil.telas));
  };

  const alterarValor = (telaId, acao, valor) => {
    setValoresTela((prev) => ({
      ...prev,
      [telaId]: { ...prev[telaId], tela: telaId, [acao]: valor },
    }));
  };

  const criarPerfil = async (e) => {
    e.preventDefault();
    if (!nomeNovo.trim()) return;
    setErro(''); setSucesso('');
    try {
      const slug = nomeNovo.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      const res = await api.post('/configuracoes/perfis/', { slug, nome: nomeNovo.trim() });
      setModalNovo(false);
      setNomeNovo('');
      await carregarTudo();
      selecionarPerfil(res.data);
    } catch (err) {
      setErro(err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Erro ao criar perfil.');
    }
  };

  const salvarPerfil = async () => {
    if (!selecionado) return;
    setSalvando(true);
    setErro(''); setSucesso('');
    try {
      const telas = Object.values(valoresTela).map((v) => ({
        tela: v.tela, leitura: !!v.leitura, gravacao: !!v.gravacao, edicao: !!v.edicao, exclusao: !!v.exclusao,
      }));
      const res = await api.patch(`/configuracoes/perfis/${selecionado.id}/`, { nome, telas });
      setPerfis((prev) => prev.map((p) => (p.id === res.data.id ? res.data : p)));
      selecionarPerfil(res.data);
      setSucesso('Perfil salvo com sucesso.');
    } catch {
      setErro('Erro ao salvar perfil.');
    } finally {
      setSalvando(false);
    }
  };

  const excluirPerfil = async (perfil) => {
    if (!window.confirm(`Excluir o perfil "${perfil.nome}"? Colaboradores que já receberam essas permissões não são afetados.`)) return;
    try {
      await api.delete(`/configuracoes/perfis/${perfil.id}/`);
      setPerfis((prev) => prev.filter((p) => p.id !== perfil.id));
      if (selecionado?.id === perfil.id) setSelecionado(null);
    } catch {
      setErro('Erro ao excluir perfil.');
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold" style={{ color: VERDE }}>Perfis de Acesso</h1>
          <AjudaTela {...AJUDA} />
        </div>
        <button
          onClick={() => setModalNovo(true)}
          className="text-white px-4 py-2 rounded-lg font-medium"
          style={{ backgroundColor: VERDE }}
        >
          + Novo perfil
        </button>
      </div>

      {erro && <div className="p-3 text-red-700 bg-red-50 border border-red-200 rounded-lg text-sm">{erro}</div>}
      {sucesso && <div className="p-3 text-green-700 bg-green-50 border border-green-200 rounded-lg text-sm">{sucesso}</div>}

      {carregando ? (
        <p className="text-gray-500">Carregando...</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow lg:col-span-1">
            <div className="p-3 border-b font-medium text-sm" style={{ color: VERDE }}>Perfis</div>
            <ul className="max-h-[600px] overflow-y-auto">
              {perfis.map((p) => (
                <li
                  key={p.id}
                  onClick={() => selecionarPerfil(p)}
                  className={`p-3 border-b cursor-pointer hover:bg-gray-50 flex items-center justify-between ${selecionado?.id === p.id ? 'bg-gray-50' : ''}`}
                  style={selecionado?.id === p.id ? { borderLeft: `3px solid ${AZUL}` } : {}}
                >
                  <span className="text-sm font-medium text-gray-800">{p.nome}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); excluirPerfil(p); }}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Excluir
                  </button>
                </li>
              ))}
              {perfis.length === 0 && (
                <li className="p-4 text-sm text-gray-500">Nenhum perfil cadastrado ainda.</li>
              )}
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow lg:col-span-2 p-4">
            {!selecionado ? (
              <p className="text-gray-500 text-sm">Selecione um perfil para editar as permissões.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Nome do perfil</label>
                  <input
                    className="w-full border rounded-lg p-2 text-sm"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-600">Permissões por tela</p>
                  <TelaPermissoesGrid modulos={modulos} valores={valoresTela} onChange={alterarValor} />
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={salvarPerfil}
                    disabled={salvando}
                    className="text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                    style={{ backgroundColor: AZUL }}
                  >
                    {salvando ? 'Salvando...' : 'Salvar perfil'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {modalNovo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-lg font-bold mb-4" style={{ color: VERDE }}>Novo perfil</h2>
            <form onSubmit={criarPerfil} className="space-y-3">
              <input
                className="w-full border rounded-lg p-2 text-sm"
                placeholder="Nome do perfil *"
                required
                value={nomeNovo}
                onChange={(e) => setNomeNovo(e.target.value)}
              />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalNovo(false)} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="submit" className="flex-1 text-white rounded-lg py-2 text-sm" style={{ backgroundColor: VERDE }}>
                  Criar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
